import { useState, useEffect, lazy, Suspense } from "react";
import { Loader2, X } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import TopHeader from "../components/TopHeader";
import FeedControls from "../components/FeedControls";
import ReviewFeed from "../components/ReviewFeed";
// Heavy modal/wizard surfaces — only mounted (and downloaded) when the
// user actually opens them. CreateReviewModal alone is ~133 KB raw and
// CameraFirstReview ~106 KB; keeping them off the initial-paint critical
// path is the single biggest win for Home FCP.
const CreateReviewModal = lazy(
  () => import("../components/CreateReviewModal")
);
const CameraFirstReview = lazy(
  () => import("../components/CameraFirstReview")
);
const OrderInitiationModal = lazy(
  () => import("../components/OrderInitiationModal")
);
const DraftReviewsManager = lazy(
  () => import("../components/DraftReviewsManager")
);
const CommentsManager = lazy(() => import("../components/CommentsManager"));
// Tab-switched views — only loaded when the user clicks into them.
const RestaurantList = lazy(() => import("./RestaurantList"));
const FriendsActivity = lazy(() => import("../components/FriendsActivity"));
const LocationPrompt = lazy(() => import("../components/LocationPrompt"));
const PostOrderReviewPrompt = lazy(() => import("../components/PostOrderReviewPrompt"));
import { locationService } from "../services/LocationService";
import { useUserProfileOptional } from "@/hooks/useUserProfile";
import { useSessionLocation } from "@/hooks/useSessionLocation";
import { useMinWidth } from "@/hooks/useMinWidth";
const TrendingPanel = lazy(() => import("@/components/TrendingPanel"));
import { SectionErrorBoundary } from "@/components/AppErrorBoundary";
import { type OrderHistoryItem } from "@/hooks/useOrderHistory";
import { Button } from "@/components/ui/button";
import AuthPromptBanner from "@/components/AuthPromptBanner";
import { useAuth } from "@/hooks/useAuth";

function TabFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export default function Home() {
  const showDesktopPanel = useMinWidth(1024);
  const [_location, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("feed");
  const [showCommentsManager, setShowCommentsManager] = useState(false);
  const userProfile = useUserProfileOptional();
  const { isAuthenticated: _isAuthenticated } = useAuth();

  // Parse URL parameters for filtering
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    const restaurant = params.get("restaurant");

    if (tab) {
      setActiveTab(tab);
    }
    if (restaurant) {
      setFeedSearchQuery(restaurant);
    }
  }, []); // Removed location dependency to prevent infinite loop
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCameraFirstReview, setShowCameraFirstReview] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] =
    useState<OrderHistoryItem | null>(null);
  const [_orderParams, setOrderParams] = useState<{
    serviceType: "delivery" | "takeout";
    timing: "asap" | "scheduled";
    scheduledTime?: string;
    location: string;
    radius?: number;
  } | null>(null);
  const [cartTotal, _setCartTotal] = useState(0);
  const [cartItemCount, _setCartItemCount] = useState(0);
  const [_searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    cuisine: [] as string[],
    location: [] as string[],
    price: [] as string[],
  });

  const { selectedLocationName, setSessionLocation } = useSessionLocation();

  const currentLocation =
    selectedLocationName ||
    (userProfile?.primaryAddress
      ? userProfile.primaryAddress.label ||
        `${userProfile.primaryAddress.streetAddress}, ${userProfile.primaryAddress.city}`
      : "Select location");
  const [activeFilters, setActiveFilters] = useState<{
    include: string[];
    exclude: string[];
  }>({ include: [], exclude: [] });
  const [feedSearchQuery, setFeedSearchQuery] = useState("");
  const [widgetFilter, setWidgetFilter] = useState<string | null>(null);
  const [selectedExperienceTypes, setSelectedExperienceTypes] = useState<
    ("dine-in" | "takeout" | "delivery")[]
  >(["dine-in", "takeout", "delivery"]);
  const [selectedDistance, setSelectedDistance] = useState<number | null>(5);

  // Location-based review prompts
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationPromptData, setLocationPromptData] = useState<{
    restaurant: Record<string, unknown>;
    timeSpent: number;
    sessionId: string;
  } | null>(null);

  // Review management integration
  const [showDraftManager, setShowDraftManager] = useState(false);
  const [showPostOrderPrompt, setShowPostOrderPrompt] = useState(false);
  const [postOrderData, setPostOrderData] = useState<
    import("../components/PostOrderReviewPrompt").OrderData | null
  >(null);
  // Get real user data from profile context
  const realUser = userProfile
    ? {
        id: String(userProfile.profile?.userId || "guest"),
        name:
          userProfile.profile?.name ||
          userProfile.profile?.firstName ||
          "Guest",
        kudozBalance: userProfile.kudozBalance || 0,
        avatar: userProfile.profile?.profilePicture,
      }
    : null;

  // Fetch unread message count (comments on user's reviews + mentions)
  // Guard with isAuthenticated so logged-out feed visitors never trigger a 401.
  const { data: unreadMessageData } = useQuery<{ count: number }>({
    queryKey: ["/api/user/unread-messages-count"],
    enabled: _isAuthenticated,
    refetchInterval: 60000, // Refresh every minute
  });

  // Mock post-order data for integration demo
  const mockPostOrderData = {
    id: "order_demo_123",
    restaurantId: "rest_demo_1",
    restaurantName: "Demo Pizza Palace",
    type: "delivery" as const,
    status: "delivered" as const,
    items: [
      {
        itemId: "item_1",
        itemName: "Margherita Pizza",
        quantity: 1,
        price: 18.99,
        specialInstructions: "Extra cheese please",
      },
      {
        itemId: "item_2",
        itemName: "Caesar Salad",
        quantity: 1,
        price: 12.99,
      },
    ],
    subtotal: 31.98,
    tax: 2.88,
    tip: 5.0,
    total: 39.86,
    deliveryAddress: "123 Main St, NYC",
    estimatedDeliveryTime: new Date(Date.now() - 35 * 60 * 1000).toISOString(), // 35 min ago
    actualDeliveryTime: new Date(Date.now() - 25 * 60 * 1000).toISOString(), // 25 min ago
    createdAt: new Date(Date.now() - 65 * 60 * 1000).toISOString(), // 65 min ago
  };

  const _handleTabChange = (tab: string) => {
    if (tab === "profile") {
      // Navigate to full profile page instead of showing inline tab
      setLocation("/profile");
    } else {
      setActiveTab(tab);
    }
  };

  const handleCreateClick = () => {
    // Go directly to camera-first review, bypassing the type selection modal
    // "From Past Order" reviews are accessible from the Profile page
    setSelectedOrderForReview(null);
    setShowCameraFirstReview(true);
  };

  // Listen for global "create review" events from ConsumerLayout's nav buttons
  useEffect(() => {
    const handler = () => handleCreateClick();
    window.addEventListener("kudoz:open-create-review", handler);
    return () =>
      window.removeEventListener("kudoz:open-create-review", handler);
  }, []);

  const _handleDineInReview = () => {
    setSelectedOrderForReview(null);
    setShowCameraFirstReview(true);
  };

  // Mock order data mapping
  const _mockOrders: { [key: string]: Record<string, unknown> } = {
    order_1: {
      id: "order_1",
      restaurantId: "rest_1",
      restaurantName: "Artisan Pizza Co",
      type: "delivery" as const,
      items: [
        {
          itemId: "item_1",
          itemName: "Margherita Pizza",
          quantity: 1,
          price: 18.0,
        },
        {
          itemId: "item_2",
          itemName: "Caesar Salad",
          quantity: 1,
          price: 12.5,
        },
      ],
    },
    order_2: {
      id: "order_2",
      restaurantId: "rest_2",
      restaurantName: "Sushi Harbor",
      type: "takeout" as const,
      items: [
        {
          itemId: "item_3",
          itemName: "Rainbow Roll",
          quantity: 1,
          price: 16.0,
        },
        { itemId: "item_4", itemName: "Spicy Tuna", quantity: 1, price: 14.0 },
        { itemId: "item_5", itemName: "Miso Soup", quantity: 2, price: 6.0 },
      ],
    },
    order_3: {
      id: "order_3",
      restaurantId: "rest_3",
      restaurantName: "Thai Spice Garden",
      type: "delivery" as const,
      items: [
        { itemId: "item_6", itemName: "Pad Thai", quantity: 1, price: 15.99 },
        { itemId: "item_7", itemName: "Tom Yum Soup", quantity: 1, price: 8.0 },
      ],
    },
  };

  const _handleOrderReview = (
    orderId: string,
    orderData?: OrderHistoryItem
  ) => {
    setSelectedOrderForReview(orderData || null);
    setShowCreateModal(true);
  };

  const handleOrderProceed = (params: {
    serviceType: "delivery" | "takeout";
    timing: "asap" | "scheduled";
    scheduledTime?: string;
    location: string;
    radius?: number;
  }) => {
    setOrderParams(params);
    setActiveTab("menus");
  };

  const _handleSearch = (query: string) => {
    setSearchQuery(query);
    console.log("Searching for:", query);
  };

  const _handleFilterChange = (newFilters: {
    cuisine: string[];
    location: string[];
    price: string[];
  }) => {
    setFilters(newFilters);
    console.log("Filters applied:", newFilters);
  };

  // Restaurant visit prompts are optional; never prompt or compete with feed
  // startup for guest visitors or users who have not granted GPS permission.
  useEffect(() => {
    if (!_isAuthenticated || !realUser?.id) return;
    let cancelled = false;
    const start = async () => {
      try {
        if (!navigator.permissions?.query) return;
        const permission = await navigator.permissions.query({ name: "geolocation" });
        if (cancelled || permission.state !== "granted") return;
        locationService.onReviewPrompt((data) => {
          if (cancelled) return;
          setLocationPromptData(data as unknown as {
            restaurant: Record<string, unknown>;
            timeSpent: number;
            sessionId: string;
          });
          setShowLocationPrompt(true);
        });
        if (!cancelled) await locationService.startLocationMonitoring(realUser.id);
        if (cancelled) locationService.stopLocationMonitoring();
      } catch {
        // Location support is optional and must not affect the feed.
      }
    };
    const timer = window.setTimeout(() => { void start(); }, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      locationService.stopLocationMonitoring();
    };
  }, [_isAuthenticated, realUser?.id]);

  const handleStartReview = (_restaurantId: string, _sessionId: string) => {
    setShowLocationPrompt(false);
    setShowCreateModal(true);

    // Mark review as created in location service
    locationService.markReviewCreated();

  };

  const handleDismissLocationPrompt = () => {
    setShowLocationPrompt(false);
    setLocationPromptData(null);
  };

  // Scroll-based controls visibility
  const [showControls, setShowControls] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollContainerRef, setScrollContainerRef] =
    useState<HTMLDivElement | null>(null);

  // Integration handlers
  const _handleShowDraftManager = () => {
    setShowDraftManager(true);
  };

  const _handleShowPostOrderDemo = () => {
    setPostOrderData(mockPostOrderData);
    setShowPostOrderPrompt(true);
  };

  const handleReviewSubmitted = (_reviewData: Record<string, unknown>) => {
    console.log("Review submitted to feed integration:", _reviewData);
    // In a real app, this would update the feed or trigger a refresh
  };

  const handleLocationChange = (
    name: string,
    lat?: number,
    lng?: number,
    addressId?: number
  ) => {
    setSessionLocation(name, lat, lng, addressId);
    setWidgetFilter(null);
  };

  const handleFiltersChange = (filters: {
    include: string[];
    exclude: string[];
  }) => {
    setActiveFilters(filters);

    // Map new tag-based filters to legacy format for ReviewFeed
    // For now, we'll just use the include filters as a simple mapping
    const legacyFilters = {
      cuisine: filters.include.filter((tag) =>
        [
          "italian",
          "mexican",
          "asian",
          "american",
          "seafood",
          "vegetarian",
          "vegan",
          "thai",
          "indian",
          "chinese",
          "mediterranean",
          "bbq",
          "fusion",
          "southern",
          "japanese",
        ].includes(tag)
      ),
      location: filters.include.filter((tag) =>
        ["fast-delivery", "on-time", "quick-prep"].includes(tag)
      ),
      price: filters.include
        .filter((tag) =>
          [
            "great-value",
            "cheap-eats",
            "budget-friendly",
            "affordable",
            "low-fees",
          ].includes(tag)
        )
        .map((tag) => {
          // Map price tags to legacy format
          if (
            [
              "great-value",
              "cheap-eats",
              "budget-friendly",
              "affordable",
              "low-fees",
            ].includes(tag)
          ) {
            return "$";
          }
          return tag;
        }),
    };
    setFilters(legacyFilters);

    console.log("Filters applied:", filters, "Legacy mapped:", legacyFilters);
  };

  const handleFeedSearchChange = (query: string) => {
    setFeedSearchQuery(query);
    setWidgetFilter(null);
    console.log("Feed search query:", query);
  };

  const handleExperienceTypeToggle = (
    type: "dine-in" | "takeout" | "delivery"
  ) => {
    setSelectedExperienceTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow deselecting all types - keep at least one
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
    console.log("Experience types toggled:", type);
  };

  const handleDistanceChange = (distance: number | null) => {
    setSelectedDistance(distance);
    console.log("Distance changed to:", distance);
  };

  // Handle scroll for controls visibility
  useEffect(() => {
    if (!scrollContainerRef) return;

    const handleScroll = () => {
      const currentScrollY = scrollContainerRef.scrollTop;

      // Only hide if scrolled down more than 50px
      if (currentScrollY < 50) {
        setShowControls(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide controls
        setShowControls(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show controls
        setShowControls(true);
      }

      setLastScrollY(currentScrollY);
    };

    scrollContainerRef.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      scrollContainerRef.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef, lastScrollY]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "feed":
        return (
          <div className="flex-1 flex flex-col overflow-hidden relative">
            <div
              className="sticky top-0 z-40 bg-background overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: showControls ? "500px" : "0px",
                opacity: showControls ? 1 : 0,
              }}
            >
              <FeedControls
                selectedLocation={currentLocation}
                activeFilters={activeFilters}
                searchQuery={feedSearchQuery}
                selectedExperienceTypes={selectedExperienceTypes}
                selectedDistance={selectedDistance}
                onLocationChange={handleLocationChange}
                onFiltersChange={handleFiltersChange}
                onSearchChange={handleFeedSearchChange}
                onExperienceTypeToggle={handleExperienceTypeToggle}
                onDistanceChange={handleDistanceChange}
                onOrderNowClick={() => setShowOrderModal(true)}
                cartTotal={cartTotal}
                cartItemCount={cartItemCount}
              />
            </div>
            {widgetFilter && (
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border-b border-border">
                <span className="text-xs text-primary font-medium flex-1 truncate">
                  Prioritising:{" "}
                  <span className="font-semibold">{widgetFilter}</span>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => setWidgetFilter(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
            <div ref={setScrollContainerRef} className="flex-1 overflow-y-auto">
              <SectionErrorBoundary section="ReviewFeed">
                <ReviewFeed
                  filter="all"
                  searchQuery={feedSearchQuery}
                  filters={filters}
                  selectedExperienceTypes={selectedExperienceTypes}
                  highlightTags={activeFilters.include.filter((tag) =>
                    [
                      "the-best",
                      "elite-reviewers",
                      "trusted-foodie-picks",
                      "top-rated-9plus",
                      "friends-recommend",
                      "following",
                      "high-trust-score",
                      "trending-now",
                    ].includes(tag)
                  )}
                  priorityQuery={widgetFilter ?? undefined}
                />
              </SectionErrorBoundary>
            </div>
          </div>
        );
      case "menus":
        return (
          <Suspense fallback={<TabFallback />}>
            <RestaurantList embedded={true} />
          </Suspense>
        );
      case "friends":
        return (
          <Suspense fallback={<TabFallback />}>
            <FriendsActivity />
          </Suspense>
        );
      default:
        return <ReviewFeed filter="all" />;
    }
  };

  return (
    <div
      className="flex flex-col h-full min-w-0 overflow-hidden"
      data-testid="home-page"
    >
      <TopHeader
        user={realUser || undefined}
        unreadAlertCount={userProfile?.unreadAlertCount || 0}
        unreadMessageCount={unreadMessageData?.count || 0}
        onNotificationsClick={() => setLocation("/profile?section=alerts")}
        onMessagesClick={() => setLocation("/profile?section=comments")}
        onProfileClick={() => setLocation("/profile")}
        onLogoClick={() => setActiveTab("feed")}
      />

      <AuthPromptBanner />

      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-hidden lg:grid lg:grid-cols-[1fr_320px]">
          <div className="flex flex-col overflow-hidden min-w-0 h-full">
            {renderTabContent()}
          </div>
          {activeTab === "feed" && showDesktopPanel && (
            <aside className="hidden lg:flex flex-col border-l border-border overflow-y-auto">
              <SectionErrorBoundary section="TrendingPanel">
                <Suspense fallback={null}>
                <TrendingPanel
                  onSelect={(name) => {
                    setWidgetFilter(name);
                    setActiveTab("feed");
                  }}
                />
                </Suspense>
              </SectionErrorBoundary>
            </aside>
          )}
        </div>
      </main>

      {showCreateModal && (
      <Suspense fallback={null}>
      <CreateReviewModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onReviewSubmitted={handleReviewSubmitted}
        detectedLocation={
          (locationPromptData?.restaurant as Record<string, unknown>)
            ?.address as string | undefined
        }
        orderData={
          selectedOrderForReview
            ? {
                id: selectedOrderForReview.id.toString(),
                // Use eatzyClientId to resolve the local restaurant UUID via backend lookup
                // API returns restaurant.profile as the client ID (not restaurant.client)
                eatzyClientId:
                  selectedOrderForReview.restaurant.profile ||
                  selectedOrderForReview.restaurant.client,
                // Pass the numeric Eatzy restaurant ID (e.g., 462076) for fallback resolution
                restaurantId: selectedOrderForReview.restaurant.id?.toString(),
                restaurantName: selectedOrderForReview.restaurant.name,
                restaurantAddress: selectedOrderForReview.address
                  ? `${selectedOrderForReview.address.address || selectedOrderForReview.address.street || ""}, ${selectedOrderForReview.address.city || ""}, ${selectedOrderForReview.address.state || ""} ${selectedOrderForReview.address.zipcode || selectedOrderForReview.address.zip || ""}`.trim()
                  : undefined,
                // API returns "type" field for service type (not "mode")
                type:
                  (selectedOrderForReview.type ||
                    selectedOrderForReview.mode) === "delivery"
                    ? "delivery"
                    : "takeout",
                orderDate: new Date(
                  selectedOrderForReview.date * 1000
                ).toISOString(),
                items: selectedOrderForReview.items.map(
                  (item: {
                    item_id?: string;
                    id?: number | string;
                    name: string;
                    quantity: number;
                    price?: number;
                  }) => ({
                    // Use item_id (menu item ID like "I463973") for linking reviews to menu items
                    // Fall back to order item id, then name if not available
                    itemId: item.item_id || item.id?.toString() || item.name,
                    itemName: item.name,
                    quantity: item.quantity,
                    price: item.price || 0,
                  })
                ),
              }
            : undefined
        }
      />
      </Suspense>
      )}

      {showOrderModal && (
      <Suspense fallback={null}>
      <OrderInitiationModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        onProceed={handleOrderProceed}
        defaultLocation={currentLocation}
      />
      </Suspense>
      )}

      {showCameraFirstReview && (
      <SectionErrorBoundary section="CameraFirstReview">
        <Suspense fallback={null}>
        <CameraFirstReview
          isOpen={showCameraFirstReview}
          onClose={() => setShowCameraFirstReview(false)}
          onReviewSubmitted={() => {
            setShowCameraFirstReview(false);
          }}
          detectedGPS={undefined}
        />
        </Suspense>
      </SectionErrorBoundary>
      )}

      {/* Location-based review prompt */}
      {showLocationPrompt && locationPromptData && (
        <Suspense fallback={null}>
        <LocationPrompt
          isOpen={showLocationPrompt}
          restaurant={
            locationPromptData.restaurant as unknown as Parameters<
              typeof LocationPrompt
            >[0]["restaurant"]
          }
          timeSpent={locationPromptData.timeSpent}
          sessionId={locationPromptData.sessionId}
          onStartReview={handleStartReview}
          onDismiss={handleDismissLocationPrompt}
        />
        </Suspense>
      )}

      {/* Draft Reviews Manager */}
      {showDraftManager && (
        <Suspense fallback={null}>
          <DraftReviewsManager
            isOpen={showDraftManager}
            onClose={() => setShowDraftManager(false)}
            onReviewSelected={(reviewData) => {
              console.log("Draft review selected for editing:", reviewData);
              // In a real app, this would pre-populate the CreateReviewModal
              setShowCreateModal(true);
            }}
          />
        </Suspense>
      )}

      {/* Post-Order Review Prompt */}
      {showPostOrderPrompt && postOrderData && (
        <Suspense fallback={null}>
        <PostOrderReviewPrompt
          isOpen={showPostOrderPrompt}
          onClose={() => {
            setShowPostOrderPrompt(false);
            setPostOrderData(null);
          }}
          orderData={
            postOrderData as unknown as Parameters<
              typeof PostOrderReviewPrompt
            >[0]["orderData"]
          }
          onReviewSubmitted={handleReviewSubmitted}
          onSkip={() => {
            setShowPostOrderPrompt(false);
            setPostOrderData(null);
            console.log("Post-order review skipped");
          }}
        />
        </Suspense>
      )}

      {/* Comments & Messages Manager */}
      {showCommentsManager && (
        <Suspense fallback={null}>
          <CommentsManager
            isOpen={showCommentsManager}
            onClose={() => setShowCommentsManager(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
